import { Icon, MenuItem } from "@material-ui/core";
import React from "react";
import styled from "styled-components";

import { notificationEvents as notificationEventsApi } from "apis-v2/callables";
import { PresentationFilters } from "common/constants";
import {
    ActionPermissionsObject,
    PermissionActionType,
    WorkspaceResourceFolderType,
    WorkspaceResourceType,
    WorkspaceUserRole,
    WorkspacePlanTier
} from "common/interfaces";
import WorkspaceController from "js/controllers/WorkspaceController";
import AppController from "js/core/AppController";
import getLogger, { LogGroup } from "js/core/logger";
import { getPresentation } from "js/core/models/presentation";
import withProgressDialog from "js/core/utilities/withProgressDialog";
import NotificationsService from "js/core/services/notifications";
import {
    ShowConfirmationDialog,
    ShowDialog,
    ShowErrorDialog,
    ShowInputDialog,
    ShowUpgradeDialog
} from "js/react/components/Dialogs/BaseDialog";
import MovePresentationToWorkspaceDialog from "js/react/components/Dialogs/MovePresentationToWorkspaceDialog";
import ShowRequestEditAccessSentSuccessfullyDialog from "js/react/components/Dialogs/ShowRequestEditAccessSentSuccessfullyDialog";
import UpgradeIcon from "js/Components/UpgradeIcon";
import { browserHistory } from "js/react/history";
import { themeColors } from "js/react/sharedStyles";
import GuestWorkspaceDialog from "js/react/components/Dialogs/GuestWorkspaceDialog";
import { ShareDialog } from "js/react/views/PresentationSettings/dialogs/ShareDialog";
import { UpgradePlanDialogType } from "js/react/views/MarketingDialogs/UpgradePlanDialog";
import ShareViewOnlyPresentationDialog from "js/react/views/PresentationSettings/dialogs/ShareViewOnlyPresentationDialog";
import CreateTeamTemplateDialog from "js/react/views/TeamResources/dialogs/CreateTeamTemplateDialog";
import { sanitizeHtmlText } from "js/core/utilities/htmlTextHelpers";
import { Icon as IconComponent } from "js/Components/Icon";
import { trackActivity } from "js/core/utilities/utilities";
import { handleUnshareFolderConflict } from "js/editor/PresentationLibrary/utils/handleUnshareFolderConflict";
import { t } from "js/i18n";
import PresentationLibraryPaneController, { DirectPermissionRequiredError, PresentationLibraryPresentation } from "js/controllers/PresentationLibraryPaneController";

const StyledMenuItem = styled(MenuItem)`
    &&& {
        color: ${themeColors.lighterGray};
    }
`;

const logger = getLogger(LogGroup.LIBRARY);

export function getPresentationContextMenuItems({ selectedPresentations }: { selectedPresentations: PresentationLibraryPresentation[] }) {
    const currentFilter = PresentationLibraryPaneController.libraryFilter;
    const currentFolderPermissions = PresentationLibraryPaneController.currentFolderPermissions;
    const isGuest = WorkspaceController.role === WorkspaceUserRole.GUEST;
    const isBasic = WorkspaceController.plan.tier === WorkspacePlanTier.BASIC;

    const canCreateResources = WorkspaceController.actionPermissions[ActionPermissionsObject.RESOURCES].create;
    const canUpgradeToCreateResources = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.RESOURCES, PermissionActionType.CREATE);
    const canUpgradeToMoveToAnotherWorkspace = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.EXTERNAL_WORKSPACES_RESOURCE_TRANSFER, PermissionActionType.USE);
    const canUpgradeToManageSharedResources = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.SHARED_RESOURCES, PermissionActionType.MANAGE);
    const canTransferResource = WorkspaceController.actionPermissions[ActionPermissionsObject.EXTERNAL_WORKSPACES_RESOURCE_TRANSFER]?.use;

    // We use it to track actions
    const trackContextAction = (action: string, additionalProps?: Record<string, unknown>) => {
        trackActivity("Presentation", action, null, null, { ...additionalProps });
    };

    const getFirstPresentationModel = () => {
        return getPresentation(selectedPresentations[0].id, { permission: "write", autoSync: true });
    };

    const getPresentationModels = (permission: "read" | "write" = "write") => {
        return Promise.all(selectedPresentations.map(p => getPresentation(p.id, { permission, autoSync: true })));
    };

    const handleEdit = event => {
        trackContextAction("Edit");
        const openInNewTab = event.metaKey || event.shiftKey;
        AppController.showEditor({ presentationId: selectedPresentations[0].id, openInNewTab });
    };

    const handlePresent = () => {
        PresentationLibraryPaneController.playPresentation(selectedPresentations[0].id, true);
        trackContextAction("Present");
    };

    const handleDelete = async () => {
        trackContextAction("MoveToTrash");
        const ownedPresentations = selectedPresentations.filter(p => p.permissions.owner);
        const collaboratedPresentations = selectedPresentations.filter(p => !p.permissions.owner);

        if (ownedPresentations.length > 0) {
            withProgressDialog({
                title: "Moving presentations to trash...",
                action: () =>
                    withProgressDialog({
                        title: "Moving presentations to trash...",
                        action: () => PresentationLibraryPaneController.trashPresentations(ownedPresentations.map(p => p.id))
                    })
                        .catch(err => {
                            ShowErrorDialog({
                                title: "Unable to move presentation(s) to trash",
                                message: err.message
                            });
                        })
            });
        }

        if (collaboratedPresentations.length > 0) {
            ShowConfirmationDialog({
                title: "Are you sure you want to stop collaborating on the selected presentation(s)?",
                message: "Shared presentations will be unshared with you and you will no longer be able to view or edit them unless they are reshared with you.",
                acceptCallback: () => {
                    withProgressDialog({
                        title: "Unsharing presentation(s)...",
                        action: () => PresentationLibraryPaneController.deletePresentations(collaboratedPresentations.map(p => p.id))
                    }).catch(async err => {
                        if (err instanceof DirectPermissionRequiredError) {
                            await handleUnshareFolderConflict({ collaboratedPresentations });
                        } else {
                            ShowErrorDialog({
                                title: "Unable to unshare presentation(s)",
                                message: err.message
                            });
                        }
                    });
                },
            });
        }
    };

    const handleDeletePermanently = () => {
        trackContextAction("DeletePermanently");
        ShowConfirmationDialog({
            title: "Are you sure you want to permanently delete this presentation?",
            message: "You can't undo this action.",
            acceptCallback: () => {
                withProgressDialog({
                    title: "Deleting presentation(s)...",
                    action: () => PresentationLibraryPaneController.deletePresentations(selectedPresentations.map(p => p.id))
                })
                    .catch(err => {
                        ShowErrorDialog({
                            title: "Unable to delete presentation(s)",
                            message: err.message
                        });
                    });
            }
        });
    };

    const handleMoveWorkspaces = () => {
        trackContextAction("MoveToAnotherWorkspace");
        ShowDialog(MovePresentationToWorkspaceDialog, {
            presentations: selectedPresentations,
            onClose: () => {
                PresentationLibraryPaneController.forceRefreshPresentations(selectedPresentations.map(p => p.id));
            }
        });
    };

    const handleRecoverFromTrash = () => {
        trackContextAction("RecoverFromTrash");
        PresentationLibraryPaneController.recoverPresentations(selectedPresentations.map(p => p.id))
            .catch(err => {
                ShowErrorDialog({
                    title: "Unable to recover presentation(s)",
                    message: err.message
                });
            });
    };

    const handleRequestEditAccess = async () => {
        trackContextAction("RequestEditAccess");
        const presentationId = selectedPresentations[0].id;

        try {
            const result = await withProgressDialog({
                title: t("Sending request…"),
                action: async () => {
                    const { hasPendingRequest } = await notificationEventsApi.getPendingPermissionRequestStatus({
                        presentationOrLinkId: presentationId,
                        permissionType: "write"
                    });
                    if (hasPendingRequest) {
                        return { isRepeatRequest: true };
                    }
                    await NotificationsService.notifyOnPermissionRequested(presentationId, "write", "");
                    return { isRepeatRequest: false };
                }
            });
            ShowDialog(ShowRequestEditAccessSentSuccessfullyDialog, {
                isRepeatRequest: Boolean(result.isRepeatRequest)
            });
        } catch (err) {
            logger.error(err, "[PresentationContextMenu] handleRequestEditAccess() failed", { presentationId });

            ShowErrorDialog({
                title: t("Unable to send edit request"),
                message: err.message
            });
        }
    };
    const handleShareViewOnlyPresentation = () => {
        trackContextAction("ShareViewOnlyPresentation");
        getPresentationModels("read")
            .then(presentations => {
                ShowDialog(ShareViewOnlyPresentationDialog, { presentation: presentations[0], onClose: () => presentations.forEach(p => p.disconnect()) });
            })
            .catch(err => {
                ShowErrorDialog({
                    title: "Unable to share presentation(s)",
                    message: err.message
                });
            });
    };

    const handleSendPresentation = () => {
        trackContextAction("Share");
        getPresentationModels()
            .then(presentations => {
                ShowDialog(ShareDialog, { presentation: presentations[0], onClose: () => presentations.forEach(p => p.disconnect()) });
            })
            .catch(err => {
                ShowErrorDialog({
                    title: "Unable to share presentation(s)",
                    message: err.message
                });
            });
    };

    const handleSharePresentation = () => {
        trackContextAction("InviteCollaborators");
        getPresentationModels()
            .then(presentations => {
                ShowDialog(ShareDialog, { presentation: presentations[0], selectedPanel: "collaborate", onClose: () => presentations.forEach(p => p.disconnect()) });
            })
            .catch(err => {
                ShowErrorDialog({
                    title: "Unable to share presentation(s)",
                    message: err.message
                });
            });
    };

    const handleRemoveFromFolder = () => {
        trackContextAction("RemoveFromFolder");
        const folderId = currentFilter.subFolderId ?? currentFilter.folderId;
        const resourceFolder = WorkspaceController.resourceFolders.find(rf => rf.id === folderId);

        withProgressDialog({
            title: `Removing presentation from ${resourceFolder.name}...`,
            action: async () => {
                try {
                    const resources = selectedPresentations.map(p => ({
                        resourceId: p.id,
                        resourceType: WorkspaceResourceType.PRESENTATION
                    }));
                    await WorkspaceController.updateWorkspaceResourceFolderResources({
                        folderId,
                        update: {
                            remove: resources
                        },
                    });
                } catch (err) {
                    logger.error(err, "[PresentationContextMenu] handleRemoveFromFolder() failed");

                    ShowErrorDialog({
                        title: "Unable to remove presentation(s)",
                        message: err.message
                    });
                }
            }
        });
    };

    const handleConvertToTemplate = async () => {
        trackContextAction("CreateTeamTemplate");
        if (canUpgradeToCreateResources) {
            ShowDialog(GuestWorkspaceDialog);
            return;
        } else if (canUpgradeToManageSharedResources) {
            ShowUpgradeDialog({
                type: UpgradePlanDialogType.UPGRADE_PLAN_TO_TEAM,
                analytics: { cta: "TeamTemplates" },
                workspaceId: WorkspaceController.workspaceId
            });
            return;
        }
        const existingTags = [];
        const presentation = await getFirstPresentationModel();
        const name = presentation.get("name") || "";
        const description = presentation.get("description") || "";
        const tags = presentation.get("tags") || [];
        presentation.disconnect();

        ShowDialog(CreateTeamTemplateDialog, {
            title: "Create a template from this presentation?",
            subTitle: "Publish a copy of this presentation for your team to start from—or save it as a draft and publish it later. View and edit this template in Team Resources at any time.",
            name,
            description,
            tags,
            existingTags,
            callback: async ({
                isPublished,
                name,
                description,
                tags,
            }) => {
                const duplicatedPresentation = await withProgressDialog({
                    title: "Duplicating presentation...",
                    action: () => PresentationLibraryPaneController.duplicatePresentation({ presentationId: presentation.id })
                });

                await PresentationLibraryPaneController.convertToPresentationTemplate(
                    duplicatedPresentation,
                    {
                        tags,
                        metadata: {
                            isTemplateConfirmed: true,
                            isPublished,
                            name,
                            description,
                        }
                    },
                    true
                );
            }
        });
    };

    const handleViewProperties = async () => {
        trackContextAction("ViewProperties");
        const presentation = await getFirstPresentationModel();
        browserHistory.push(`/${presentation.id}/settings/properties`);
    };

    const handleExportPresentation = () => {
        trackContextAction("Export");
        browserHistory.push(`/${selectedPresentations[0].id}/settings/export?openDialog=true`);
    };

    const handleRenamePresentation = async () => {
        trackContextAction("Rename");
        const presentationId = selectedPresentations[0].id;

        const presentationName = await ShowInputDialog({
            title: "Enter a name for your presentation",
            value: selectedPresentations[0].name,
            trimInput: true,
            onChange: str => sanitizeHtmlText(str)
        });

        if (!presentationName) {
            return;
        }

        await PresentationLibraryPaneController.markPresentationsDirty([presentationId]);
        try {
            const presentation = await getPresentation(presentationId, { permission: "write", autoSync: false });
            try {
                await presentation.rename(presentationName);
            } finally {
                await presentation.disconnect();
            }
        } catch (err) {
            logger.error(err, "[PresentationContextMenu] handleRenamePresentation failed");
            ShowErrorDialog({ title: "Unable to rename presentation", message: err?.message ?? String(err) });
        } finally {
            await PresentationLibraryPaneController.forceRefreshPresentations([presentationId]);
        }
    };

    const canWriteFolder = currentFilter.type !== PresentationFilters.FOLDER || currentFolderPermissions.write.object;
    const canCreate = isBasic || canCreateResources || isGuest;
    const lockCreate = !canCreate && canUpgradeToCreateResources;

    const MenuItems = [];

    const addItem = ({
        key,
        lockedKey,
        label,
        icon,
        divider = false,
        onClick,
        enabled = false,
        locked = false,
    }: {
        key: string;
        lockedKey?: string;
        label: React.ReactNode;
        icon?: React.ReactNode;
        divider?: boolean;
        onClick?: (event: React.MouseEvent) => void;
        enabled?: boolean;
        locked?: boolean;
    }) => {
        if (enabled) {
            MenuItems.push(
                <MenuItem key={key} divider={divider} onClick={onClick}>
                    {icon}
                    {label}
                </MenuItem>
            );
        } else if (locked) {
            MenuItems.push(
                <StyledMenuItem key={lockedKey ?? key} divider={divider}>
                    <UpgradeIcon style={{ marginRight: 6 }} />
                    {label}
                </StyledMenuItem>
            );
        }
    };

    const renderMoveToAnotherWorkspace = (isOwner: boolean, includeDivider = false) => {
        if (AppController.workspacesMetadata.length <= 1) {
            return;
        }

        addItem({
            key: "move_workspace",
            label: "Move To Another Workspace",
            icon: <Icon>business</Icon>,
            divider: includeDivider,
            onClick: isOwner
                ? handleMoveWorkspaces
                : () => {
                    trackContextAction("MoveToAnotherWorkspace");
                    ShowDialog(GuestWorkspaceDialog);
                },
            enabled: isOwner && canWriteFolder && (canTransferResource || canUpgradeToMoveToAnotherWorkspace),
            locked: !isOwner && canUpgradeToMoveToAnotherWorkspace,
        });
    };

    if (selectedPresentations.length === 1) {
        const presentation = selectedPresentations[0];

        if (currentFilter.type === PresentationFilters.TRASH) {
            addItem({
                key: "recover",
                label: "Recover From Trash",
                icon: <Icon>restore_from_trash</Icon>,
                onClick: handleRecoverFromTrash,
                enabled: true,
            });
            addItem({
                key: "delete-permanently",
                label: "Delete Permanently",
                icon: <Icon>delete_forever</Icon>,
                onClick: handleDeletePermanently,
                enabled: true,
            });
        } else {
            addItem({
                key: "present-all",
                label: "Present",
                icon: <Icon>present_to_all</Icon>,
                onClick: handlePresent,
                enabled: true,
            });

            if (presentation.permissions.owner || presentation.permissions.write) {
                addItem({
                    key: "edit",
                    label: "Edit",
                    icon: <Icon>edit</Icon>,
                    onClick: handleEdit,
                    enabled: canCreate,
                    locked: lockCreate,
                });

                MenuItems.push(<hr key="divider2" />);

                addItem({
                    key: "send",
                    label: "Share...",
                    icon: <Icon>share</Icon>,
                    onClick: handleSendPresentation,
                    enabled: true,
                });

                addItem({
                    key: "share",
                    label: "Invite Collaborators...",
                    icon: <Icon>person_add_alt_1</Icon>,
                    divider: true,
                    onClick: handleSharePresentation,
                    enabled: canCreate,
                    locked: lockCreate,
                });

                addItem({
                    key: "rename",
                    lockedKey: "rename_locked",
                    label: "Rename...",
                    icon: (
                        <IconComponent
                            large
                            color="#666666"
                            style={{ marginRight: "6px" }}
                        >
                            edit_square
                        </IconComponent>
                    ),
                    onClick: () => handleRenamePresentation(),
                    enabled: canCreate,
                    locked: lockCreate,
                });

                const canViewProperties = presentation.permissions.owner || (!isBasic && (canCreateResources || isGuest));
                const lockProperties = !canViewProperties && !isGuest && (isBasic || canUpgradeToCreateResources);

                addItem({
                    key: "properties",
                    lockedKey: "properties_locked",
                    label: "View Properties...",
                    icon: <Icon>info</Icon>,
                    onClick: handleViewProperties,
                    enabled: canViewProperties,
                    locked: lockProperties,
                });

                addItem({
                    key: "export",
                    label: "Export...",
                    icon: <Icon>cloud_download</Icon>,
                    divider: true,
                    onClick: () => handleExportPresentation(),
                    enabled: WorkspaceController.actionPermissions[ActionPermissionsObject.EXPORT_PRESENTATION].use,
                });
            } else {
                addItem({
                    key: "send",
                    label: "Share...",
                    icon: <Icon>share</Icon>,
                    divider: true,
                    onClick: handleShareViewOnlyPresentation,
                    enabled: true,
                });
            }

            const canDuplicate = !isBasic && canWriteFolder && canCreateResources && !isGuest;
            const lockDuplicate = !canDuplicate && (isBasic || (!isGuest && canUpgradeToCreateResources) || (isGuest && WorkspaceController.isMultiUserWorkspace));

            addItem({
                key: "duplicate",
                lockedKey: "duplicate_locked",
                label: "Duplicate",
                icon: <Icon>content_copy</Icon>,
                onClick: () => {
                    trackContextAction("Duplicate");
                    PresentationLibraryPaneController.handleDuplicate(selectedPresentations[0].id);
                },
                enabled: canDuplicate,
                locked: lockDuplicate,
            });

            if (presentation.permissions.owner || presentation.permissions.write) {
                const canManageShared = !isBasic && WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].manage;
                const lockTemplate = !canManageShared && (isBasic || canUpgradeToCreateResources || canUpgradeToManageSharedResources);

                addItem({
                    key: "convert-to-template",
                    lockedKey: "convert-to-template_locked",
                    label: "Create Team Template...",
                    icon: <Icon>file_copy</Icon>,
                    onClick: handleConvertToTemplate,
                    enabled: canManageShared,
                    locked: lockTemplate,
                });
            }

            renderMoveToAnotherWorkspace(presentation.permissions.owner, false);

            MenuItems.push(<hr key="divider4" />);

            if (currentFilter.type === PresentationFilters.FOLDER) {
                const folderId = currentFilter.subFolderId ?? currentFilter.folderId;
                const resourceFolder = WorkspaceController.resourceFolders.find(rf => rf.id === folderId);
                const isSharedFolder = resourceFolder?.folderType === WorkspaceResourceFolderType.SHARED ||
                    resourceFolder?.folderType === WorkspaceResourceFolderType.SHARED_DEFAULT;

                if (!isSharedFolder || currentFolderPermissions.write.object) {
                    const label = isSharedFolder
                        ? `Remove from ${currentFilter.subFolderId ? "Subfolder" : "Team Folder"}`
                        : "Remove from Folder";

                    addItem({
                        key: "remove",
                        lockedKey: isSharedFolder ? "remove_team_locked" : "remove_locked",
                        label,
                        icon: <Icon>remove_circle</Icon>,
                        divider: true,
                        onClick: handleRemoveFromFolder,
                        enabled: !isBasic,
                        locked: isBasic,
                    });
                }
            }

            if (!presentation.permissions.owner && !presentation.permissions.write) {
                addItem({
                    key: "request-edit-access",
                    label: t("Request Edit Access"),
                    icon: <Icon>edit</Icon>,
                    onClick: handleRequestEditAccess,
                    enabled: true,
                });
            }

            if (presentation.permissions.owner) {
                addItem({
                    key: "trash",
                    label: "Move To Trash",
                    icon: <Icon>delete</Icon>,
                    onClick: handleDelete,
                    enabled: true,
                });
            } else {
                addItem({
                    key: "unshare",
                    lockedKey: "unshare_locked",
                    label: "Unshare with me",
                    icon: <Icon>delete</Icon>,
                    onClick: handleDelete,
                    enabled: !isBasic,
                    locked: isBasic,
                });
            }
        }
    } else {
        if (currentFilter.type === PresentationFilters.TRASH) {
            addItem({
                key: "recover",
                label: "Recover From Trash",
                icon: <Icon>restore_from_trash</Icon>,
                onClick: handleRecoverFromTrash,
                enabled: true,
            });
            addItem({
                key: "delete-permanently",
                label: "Delete Permanently",
                icon: <Icon>delete_forever</Icon>,
                onClick: handleDeletePermanently,
                enabled: true,
            });
        } else {
            if (currentFilter.type === PresentationFilters.FOLDER) {
                addItem({
                    key: "remove",
                    lockedKey: "remove_locked",
                    label: "Remove from Folder",
                    icon: <Icon>remove_circle</Icon>,
                    onClick: handleRemoveFromFolder,
                    enabled: !isBasic,
                    locked: isBasic,
                });
            }

            const hasOwned = selectedPresentations.some(p => p.permissions.owner);
            const hasShared = selectedPresentations.some(p => !p.permissions.owner);

            renderMoveToAnotherWorkspace(hasOwned, true);

            const label = hasOwned && hasShared
                ? "Move to Trash or Unshare"
                : hasOwned
                    ? "Move to Trash"
                    : "Unshare with me";

            addItem({
                key: "trash",
                lockedKey: "trash_locked",
                label,
                icon: <Icon>delete</Icon>,
                onClick: handleDelete,
                enabled: !isBasic,
                locked: isBasic,
            });
        }
    }

    return MenuItems;
}
